import * as THREE from "three";
import type { VFX } from "../../../../../art/vfx.js";
import { KitClock } from "../skins/kit/clock";
import { PET_FX_BUDGET, type PetCoatFxSpec, type PetFxBurst, type PetFxKind } from "../skins/kit/petCoatTypes";

/** The VFX module's raw particle spawn (art/vfx.js `spawn`, not in its .d.ts), for the coat's own particle shapes. */
type Spawner = { spawn(p: Record<string, unknown>): unknown };
/** A pet's world height at scale 1 (the models are built to ~0.20): every size and speed below is in these. */
export const PET_HEIGHT = 0.2;
/** Particle shapes in pet heights (sizes, speeds a second) and seconds. */
const SHAPE = {
  sparkleSize: 0.30, sparkleRise: 1.4, glintSize: 0.34, moteSize: 0.20, moteRise: 1.6, emberSize: 0.24, bubbleSize: 0.30,
  snowSize: 0.20, snowFall: 0.9, snowLife: 1.8, wispSize: 0.45, wispRise: 1.3, wispLife: 1.3, leafSize: 0.42,
  leafFall: 0.8, leafLife: 2.2, flashSize: 1.2,
} as const;
const _at = new THREE.Vector3(), _p = new THREE.Vector3(), _v = new THREE.Vector3();
const rnd = (a: number, b: number): number => a + Math.random() * (b - a);

/** Where a rig part of the pet is now (world), written into `out`; null when the model lacks it. */
export type PetNodeAt = (node: string, out: THREE.Vector3) => THREE.Vector3 | null;

/** A burst made ready: its colour as linear RGB 0–1 (converted once, not per burst). */
interface Ready { readonly kind: PetFxKind; readonly count: number; readonly color: [number, number, number] }
const ready = (b: PetFxBurst, max: number): Ready => {
  const c = new THREE.Color(b.colour);
  return { kind: b.kind, count: Math.max(0, Math.min(max, b.count)), color: [c.r, c.g, c.b] };
};

/**
 * A pet coat's own effects (skins v2, kit/petCoatTypes.ts): an ambient burst off one of its rig parts every few
 * seconds, and its flourish — the coat's `flourish` clip played in place of some fidgets, with a burst at its peak.
 * Owned by the pet's body (views/petBody.ts) wherever it is drawn with a VFX module (the battle, the wild pet, the hub's
 * lawn). Gated like the tower kits (KitClock: nothing under Reduced effects or at the lowest tiers); counts clamped to
 * PET_FX_BUDGET whatever the data asks. Per frame it only counts down: bursts allocate only what the VFX module does.
 */
export class PetCoatFx {
  private ambientIn: number;
  private peakIn = -1;
  private readonly ambient: Ready | null;
  private readonly also: Ready | null;
  private readonly burst: Ready | null;

  constructor(readonly spec: PetCoatFxSpec | null, private readonly rand: () => number = Math.random) {
    const a = spec?.ambient;
    this.ambient = a ? ready(a, PET_FX_BUDGET.AmbientParticles) : null;
    this.also = a?.also && this.ambient ? ready(a.also, PET_FX_BUDGET.AmbientParticles - this.ambient.count) : null;
    this.burst = spec ? ready(spec.flourish.burst, PET_FX_BUDGET.FlourishParticles) : null;
    this.ambientIn = a ? rand() * a.every[1] : Infinity;
  }

  /** The clip a fidget plays instead this time (the coat's flourish, its share of the time), or null for the fidget. */
  pick(has: (clip: string) => boolean): string | null {
    const f = this.spec?.flourish;
    return f && has(f.clip) && this.rand() < f.share ? f.clip : null;
  }

  /** Its flourish clip started: the burst comes at the clip's peak. */
  flourished(): void { if (this.spec) this.peakIn = this.spec.flourish.peak; }

  /**
   * Every frame: the countdowns; a burst when one comes due (and the effects are on). `scale`: the pet's draw scale;
   * `vfx` is asked for the module only when a burst is due (the hub loads its sprites on first use).
   */
  update(dt: number, scale: number, at: PetNodeAt, vfx: (() => VFX | null) | null): void {
    const s = this.spec;
    if (!s) return;
    const h = PET_HEIGHT * scale;
    if (this.peakIn >= 0 && (this.peakIn -= dt) < 0) {
      const f = s.flourish, fx = vfx?.() ?? null;
      if (fx && this.burst && KitClock.gates().particles && PetCoatFx.where(at, f.node ?? s.ambient.node, h, 0.4, _at)) {
        PetCoatFx.emit(fx, this.burst, _at, h * 0.55, h);
        fx.flash(_at, h * SHAPE.flashSize, this.burst.color);
      }
    }
    if ((this.ambientIn -= dt) > 0) return;
    const a = s.ambient;
    this.ambientIn = Math.max(PET_FX_BUDGET.MinEvery, a.every[0] + this.rand() * (a.every[1] - a.every[0]));
    if (!this.ambient || !KitClock.gates().particles) return;
    const fx = vfx?.() ?? null;
    if (!fx) return;
    if (!PetCoatFx.where(at, a.node, h, a.lift ?? 0.3, _at)) return;
    const r = h * (a.radius ?? 0.35);
    PetCoatFx.emit(fx, this.ambient, _at, r, h);
    if (this.also) PetCoatFx.emit(fx, this.also, _at, r, h);
  }

  /** The node's world position lifted `lift` pet heights (falling back to the body), into out; null without either. */
  private static where(at: PetNodeAt, node: string, h: number, lift: number, out: THREE.Vector3): THREE.Vector3 | null {
    const p = at(node, out) ?? at("body", out);
    if (p) p.y += lift * h;
    return p;
  }

  /** One burst of its kind round `pos` (radius r, pet height h). */
  static emit(fx: VFX, b: Ready, pos: THREE.Vector3, r: number, h: number): void {
    const n = b.count, color = b.color;
    if (n <= 0) return;
    const sp = fx as unknown as Spawner;
    switch (b.kind) {
      case "sparkles": fx.sparkles(pos, n, { color, radius: r, rise: SHAPE.sparkleRise * h, life: 0.9, size: SHAPE.sparkleSize * h }); return;
      case "glints": fx.sparkles(pos, n, { color, radius: r, rise: 0.1 * h, life: 0.6, size: SHAPE.glintSize * h }); return;
      case "motes": fx.sparkles(pos, n, { color, radius: r, rise: SHAPE.moteRise * h, life: 1.6, size: SHAPE.moteSize * h }); return;
      case "embers": fx.embers(pos, n, { color, radius: r, life: 1.0, size: SHAPE.emberSize * h }); return;
      case "bubbles": fx.bubbles(pos, n, { color, radius: r, size: SHAPE.bubbleSize * h, life: 0.9 }); return;
      case "snow":
        for (let i = 0; i < n; i++) {
          _p.set(pos.x + rnd(-r, r), pos.y + rnd(-0.2, 0.2) * h, pos.z + rnd(-r, r));
          sp.spawn({ pos: _p, vel: _v.set(rnd(-0.2, 0.2) * h, -SHAPE.snowFall * h, rnd(-0.2, 0.2) * h), frame: "star", life: SHAPE.snowLife * rnd(0.8, 1.2), size0: SHAPE.snowSize * h, size1: SHAPE.snowSize * h * 0.7, color0: color, bright: 1.4, fadeIn: 0.2, fadeOut: 0.35, rot: rnd(0, 6.3), rotVel: rnd(-1.5, 1.5), floor: pos.y - 1.6 * h });
        }
        return;
      case "wisps":
        for (let i = 0; i < n; i++) {
          _p.set(pos.x + rnd(-r, r), pos.y, pos.z + rnd(-r, r));
          sp.spawn({ pos: _p, anim: "smoke", vel: _v.set(rnd(-0.25, 0.25) * h, SHAPE.wispRise * h, rnd(-0.25, 0.25) * h), drag: 1.2, life: SHAPE.wispLife * rnd(0.8, 1.2), size0: SHAPE.wispSize * h * 0.5, size1: SHAPE.wispSize * h, color0: color, alpha: 0.6, additive: false, fadeIn: 0.1, fadeOut: 0.6, rot: rnd(0, 6.3), rotVel: rnd(-1.2, 1.2) });
        }
        return;
      case "leaves": case "petals":
        for (let i = 0; i < n; i++) {
          const shade = rnd(0.8, 1.15);
          _p.set(pos.x + rnd(-r, r), pos.y + rnd(-0.1, 0.1) * h, pos.z + rnd(-r, r));
          sp.spawn({ pos: _p, vel: _v.set(rnd(-0.3, 0.3) * h, -SHAPE.leafFall * h, rnd(-0.3, 0.3) * h), frame: "leaf", gravity: 0.05, drag: 0.3, life: SHAPE.leafLife * rnd(0.8, 1.2), size0: SHAPE.leafSize * h * (b.kind === "petals" ? 0.7 : 1), color0: [color[0] * shade, color[1] * shade, color[2] * shade], additive: false, rot: rnd(0, 6.3), rotVel: rnd(-3, 3), fadeIn: 0.15, fadeOut: 0.3, floor: pos.y - 1.8 * h });
        }
        return;
    }
  }
}
